import React, { useContext, useMemo } from 'react'

import { ArcadeModalHeader, IsInArcadeModalContext, useArcadeModalActionOverride } from '@cash-design-system/react'
import { useArcadeMigrationFeatureFlag } from '@cash-web/shared-data-access-arcade-migration'
import {
  IsInModalContext,
  ModalActionHeader,
  ModalBackAction,
  ModalCloseAction,
  ModalPaneHeader,
  useModalActionsOverride,
  useModalSharedButtonProps,
} from '@cash-web/shared-feature-modal'
import { useJourneyScope } from '@cash-web/shared-journey-data-access-context'

export interface BlockerActionHeaderProps {
  onBack?: () => void
  onClose?: () => void
}

/**
 * The actual rendering of the action header is handled by the modal.
 */
const ModalBlockerActionHeader = ({
  actionHeaderOverride,
  onBack,
  onClose,
}: {
  actionHeaderOverride?: React.ReactElement
  onBack?: () => void
  onClose?: () => void
}) => {
  // TODO: remove useModalActionsOverride when all modals have been migrated to ArcadeModal
  useModalActionsOverride(actionHeaderOverride)

  useArcadeModalActionOverride(
    useMemo(
      () => ({
        type: 'buttons' as const,
        ...(onBack ? { backButtonProps: { onBack, ariaLabel: 'Go back' } } : {}),
        ...(onClose ? { closeButtonProps: { onClose, ariaLabel: 'Close' } } : {}),
      }),
      [onBack, onClose]
    )
  )
  return null
}

/**
 * Handles the case when a blocker wants to override the behavior of the action header.
 * If the blocker is in a modal, we'll use the useArcadeModalActionOverride hook to override the action header.
 * If the blocker is not in a modal, it will return the default action header that should be rendered by the blocker.
 * @param onBack - The function to call when the back button is clicked.
 * @param onClose - The function to call when the close button is clicked.
 */
export const BlockerActionHeader = ({ onBack, onClose }: BlockerActionHeaderProps) => {
  const isInModal = useContext(IsInModalContext)
  const isInArcadeModal = useContext(IsInArcadeModalContext)
  const journeyScope = useJourneyScope()

  const isArcadeMigrationEnabled = useArcadeMigrationFeatureFlag('modal')
  const sharedModalButtonProps = useModalSharedButtonProps({
    closeButtonProps: { onClose },
    backButtonProps: { onBack },
  })

  if (!onBack && !onClose) {
    return null
  }

  if (isArcadeMigrationEnabled) {
    if (isInArcadeModal) {
      return <ModalBlockerActionHeader onBack={onBack} onClose={onClose} />
    }

    return <ArcadeModalHeader {...sharedModalButtonProps} />
  }

  const actionHeader =
    journeyScope.target === 'fullscreen' ? (
      <ModalPaneHeader>{onBack && <ModalBackAction onBack={onBack} />}</ModalPaneHeader>
    ) : (
      <ModalActionHeader>
        {onClose && <ModalCloseAction onClose={onClose} />}
        {onBack && <ModalBackAction onBack={onBack} />}
      </ModalActionHeader>
    )

  if (isInModal) {
    return <ModalBlockerActionHeader onBack={onBack} onClose={onClose} actionHeaderOverride={actionHeader} />
  }
  return actionHeader
}
